import React, { useState, useEffect, useMemo } from "react";
import { HiOutlineXMark } from "react-icons/hi2";
import ResponsiveCDNImage from "../common/image/responsive-cdn-image";

// 모달 렌더링에 필요한 최소한의 데이터 필드 정의
export interface ReviewModalData {
  user?: {
    maskedName?: string | null;
  } | null;
  orderType?: string | null;
  createdAt: string | number | Date;
  reviewImages?: Array<{ img?: string | null } | null> | null;
  content?: string | null;
}

interface ReviewDetailModalProps {
  review?: ReviewModalData | null;
  onClose: () => void;
}

const ReviewDetailModal: React.FC<ReviewDetailModalProps> = ({
  review,
  onClose,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const reviewImages = useMemo(() => {
    return review?.reviewImages?.filter((img) => img?.img) as
      | Array<{ img: string }>
      | undefined;
  }, [review?.reviewImages]);

  // 모든 이미지 미리 로드 (preload)
  useEffect(() => {
    if (reviewImages && reviewImages.length > 0) {
      reviewImages.forEach((image) => {
        const img = new Image();
        img.src = image.img;
      });
    }
  }, [reviewImages]);

  // 리뷰가 변경되면 첫 번째 이미지로 리셋
  useEffect(() => {
    setSelectedImageIndex(0);
  }, [review]);

  if (!review) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-[90%] max-w-[500px] max-h-[90vh] relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 overflow-y-auto max-h-[90vh]">
          <div className="flex justify-between items-start gap-2 mb-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-semibold">
                  {review.user?.maskedName || "알 수 없음"}
                </span>
                <span className="font-medium text-gray-600">
                  {review.orderType === "PACK" ? "예약구매" : "실시간구매"}
                </span>
              </div>
              <div className="text-gray-500 text-sm">
                {new Date(review.createdAt).toLocaleDateString("ko-KR", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}{" "}
                {new Date(review.createdAt).toLocaleTimeString("ko-KR", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 active:text-gray-700"
            >
              <HiOutlineXMark className="w-6 h-6" />
            </button>
          </div>

          {reviewImages && reviewImages.length > 0 && (
            <div className="mb-4">
              {/* 대표 이미지 */}
              <div className="mb-3 relative">
                <ResponsiveCDNImage
                  key={selectedImageIndex}
                  src={reviewImages[selectedImageIndex]?.img}
                  alt={`리뷰 이미지 ${selectedImageIndex + 1}`}
                  className="w-full h-[300px] object-cover rounded-lg"
                  sizeRatio={1}
                  quality={90}
                />
              </div>
              {/* 썸네일 리스트 (여러 장일 때만 표시) */}
              {reviewImages.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {reviewImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        selectedImageIndex === index
                          ? "border-primary"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <ResponsiveCDNImage
                        src={image.img}
                        alt={`리뷰 이미지 썸네일 ${index + 1}`}
                        className="w-full h-full object-cover"
                        sizeRatio={0.25}
                        quality={80}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="text-gray-700 whitespace-pre-wrap">
            {review.content}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDetailModal;
